import { useState, useRef, useEffect, useMemo } from "react";
import { Row, Col, Modal, Table, Badge, Button as BootstrapButton } from "react-bootstrap";
import { useFetch } from "../../../hooks/api";
import ReactTable from "react-table-6";
import ReactTablePagination from "../../../components/custom/growth/ProductsCVTablePagination";
import Loading from "../../../components/core/blocks/Loading";
import useColumnSelector from "../../../hooks/useColumnSelector";
import { formatCurrency } from "../../../utils/formatNumber";
import { Button } from "../../../components/core/basic/Button";
import { FaPlusCircle, FaEdit, FaMinusCircle } from "react-icons/fa";
import { NumberInput } from "../../../components/fields/base/NumberInput";
import axios from "axios";
import { getURLPrefix } from "../../../utils/getUrlPrefix";
import { keepPreviousData, useQueryClient } from "@tanstack/react-query";
import { Chart } from "@highcharts/react";
import { trellisPalette } from "../../../components/custom/analytics/palettes";
import { cut } from "../../../utils/formatText";
import NoImage from "../../../assets/images/utility/no_image.png";
import { RiOrganizationChart } from "react-icons/ri";
import { RiTable2 } from "react-icons/ri";
import { Checkbox } from "../../../components/fields/base/Checkbox";
import { useSimpleCan } from "../../../config/casl/casl";
import { useTranslation } from "react-i18next";

const URL_PREFIX = getURLPrefix();

const RelationshipSection = ({ pricingPlan, marketPlace }) => {
  const { t } = useTranslation(["pricingv2"]);
  const actionTypeOptions = [
    { label: t(($) => $.pricingv2["Not Set"]), value: "not_set" },
    { label: t(($) => $.pricingv2["Equal"]), value: "match" },
    { label: t(($) => $.pricingv2["Less than"]), value: "decrease" },
    { label: t(($) => $.pricingv2["Greater than"]), value: "increase" },
  ];

  const queryClient = useQueryClient();
  const userCanUpdatePricingPlans = useSimpleCan("update", "PricingPlan");

  const [search, setSearch] = useState("");
  const [colToggle] = useState();
  const [showAddRelationship, setShowAddRelationship] = useState(false);
  const [filteredPlans, setFilteredPlans] = useState(null);
  const [parentPlan, setParentPlan] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);

  const [relatedMinAction, setRelatedMinAction] = useState(actionTypeOptions[3]);
  const [relatedMinValue, setRelatedMinValue] = useState(0);
  const [relatedMinUnit, setRelatedMinUnit] = useState("percent");
  const [newMinPrice, setNewMinPrice] = useState(null);
  const [newMaxPrice, setNewMaxPrice] = useState(null);

  const [relatedMaxAction, setRelatedMaxAction] = useState(actionTypeOptions[3]);
  const [relatedMaxValue, setRelatedMaxValue] = useState(0);
  const [relatedMaxUnit, setRelatedMaxUnit] = useState("percent");

  const [relatedMode, setRelatedMode] = useState("new");
  const [savingRelationship, setSavingRelationship] = useState(false);

  const [showRemoveModal, setShowRemoveModal] = useState(false);

  const [tab, setTab] = useState("chart_view");

  const tableRef = useRef();

  const { data, isLoading } = useFetch(
    ["pricingPlanRelationships", marketPlace.marketPlace],
    `/pricing/pricingplan/relationships?pricing_plan_id=${pricingPlan.id}`,
    {},
    {
      select: (d) => d.data,
      placeholderData: keepPreviousData,
    },
  );

  const { data: planNames, isLoading: planIsLoading } = useFetch(
    ["pricingPlanNames", marketPlace.marketPlace],
    `/pricing/pricingplan/names`,
    {},
    {
      select: (d) => d.data,
      placeholderData: keepPreviousData,
    },
  );

  const hideModal = () => {
    setFilteredPlans([]);
    setParentPlan(null);
    setSelectedPlan(null);

    setRelatedMinAction(actionTypeOptions[3]);
    setRelatedMinValue(null);
    setRelatedMinUnit("percent");
    setNewMinPrice(null);
    setNewMaxPrice(null);

    setRelatedMaxAction(actionTypeOptions[3]);
    setRelatedMaxValue(null);
    setRelatedMaxUnit("percent");

    setRelatedMode("new");
    setSavingRelationship(false);
    setShowAddRelationship(false);
  };

  const hideRemoveModal = () => {
    setSelectedPlan(null);
    setSavingRelationship(false);
    setShowRemoveModal(false);
  };

  const sendRelated = () => {
    if (!selectedPlan) return;

    const rule = {};
    setSavingRelationship(true);

    if (relatedMinAction.value !== "not_set") {
      rule.min_price = {
        unit: relatedMinUnit,
        value: parseFloat(relatedMinValue),
        action: relatedMinAction.value,
      };
    }
    if (relatedMaxAction.value !== "not_set") {
      rule.max_price = {
        unit: relatedMaxUnit,
        value: parseFloat(relatedMaxValue),
        action: relatedMaxAction.value,
      };
    }

    const data = {
      rule: rule,
      parent_plan_id: parentPlan.id,
      related_plan_id: selectedPlan.id,
    };

    axios.post(`${URL_PREFIX}/pricing/pricingplan/relationships/set`, data).then(() => {
      hideModal();
      queryClient.invalidateQueries({
        queryKey: ["pricingPlanRelationships"],
      });
    });
  };

  const sendRemoveRelationship = () => {
    if (!selectedPlan) return;

    setSavingRelationship(true);

    const data = {
      related_plan_id: selectedPlan.id,
    };

    axios.post(`${URL_PREFIX}/pricing/pricingplan/relationships/remove`, data).then(() => {
      hideRemoveModal();
      queryClient.invalidateQueries({
        queryKey: ["pricingPlanRelationships"],
      });
    });
  };

  const getPricingPlan = (id) => {
    if (!data) return null;

    const plan = data.find((d) => {
      return d.id === id;
    });
    return plan;
  };

  const getPrice = (plan, price, isMin) => {
    if (!plan) return null;

    if (!plan?.relationship) return null;

    let rule = null;
    if (plan.relationship_rules.min_price && isMin) {
      rule = plan.relationship_rules.min_price;
    } else if (plan.relationship_rules.max_price && !isMin) {
      rule = plan.relationship_rules.max_price;
    }
    if (!rule) {
      return price;
    }

    if (rule.unit === "percent") {
      return (price * rule.value) / 100;
    }
    return price + rule.value;
  };

  const calcNewPrice = () => {
    if (relatedMinAction.value === "match") {
      setNewMinPrice(parentPlan.best_price);
    } else if (relatedMinAction.value === "decrease") {
      if (relatedMinUnit === "percent") {
        setNewMinPrice((parentPlan.best_price * (parseFloat(relatedMinValue) * -1)) / 100);
      } else {
        setNewMinPrice(parentPlan.best_price - parseFloat(relatedMinValue));
      }
    } else if (relatedMinAction.value === "increase") {
      if (relatedMinUnit === "percent") {
        setNewMinPrice((parentPlan.best_price * parseFloat(relatedMinValue)) / 100);
      } else {
        setNewMinPrice(parentPlan.best_price + parseFloat(relatedMinValue));
      }
    }

    if (relatedMaxAction.value === "match") {
      setNewMaxPrice(parentPlan.best_price);
    } else if (relatedMaxAction.value === "decrease") {
      if (relatedMaxUnit === "percent") {
        setNewMaxPrice((parentPlan.best_price * (parseFloat(relatedMaxValue) * -1)) / 100);
      } else {
        setNewMaxPrice(parentPlan.best_price - parseFloat(relatedMaxValue));
      }
    } else if (relatedMaxAction.value === "increase") {
      if (relatedMaxUnit === "percent") {
        setNewMaxPrice((parentPlan.best_price * parseFloat(relatedMaxValue)) / 100);
      } else {
        setNewMaxPrice(parentPlan.best_price + parseFloat(relatedMaxValue));
      }
    }
  };

  const calcMinPrice = (plan) => {
    if (plan.relationship) {
      if (plan.relationship_rules.min_price) {
        let rule = "";
        if (plan.relationship_rules.min_price.action === "increase") {
          rule += t(($) => $.pricingv2["Increase by "]);
        } else if (plan.relationship_rules.min_price.action === "decrease") {
          rule += t(($) => $.pricingv2["Decrease by "]);
        }
        if (plan.relationship_rules.min_price.unit === "percent") {
          rule += `${plan.relationship_rules.min_price.value.toFixed(2)}%`;
        } else {
          rule += `${formatCurrency(plan.relationship_rules.min_price.value, {
            marketPlace: marketPlace,
          })}`;
        }

        const p = getPricingPlan(plan.relationship);
        return (
          <>
            <div>
              {formatCurrency(getPrice(plan, p.best_price, true), {
                marketPlace: marketPlace,
              })}
            </div>
            <small>
              <span>
                {formatCurrency(p.best_price, {
                  marketPlace: marketPlace,
                })}
              </span>{" "}
              <span>{rule}</span>
            </small>
          </>
        );
      }
      return `${formatCurrency(plan.min_price, {
        marketPlace: marketPlace,
      })}`;
    }
    return formatCurrency(plan.min_price, {
      marketPlace: marketPlace,
    });
  };

  const calcMaxPrice = (plan) => {
    if (plan.relationship) {
      if (plan.relationship_rules.max_price) {
        let rule = "";
        if (plan.relationship_rules.max_price.action === "increase") {
          rule += t(($) => $.pricingv2["Increase by "]);
        } else if (plan.relationship_rules.max_price.action === "decrease") {
          rule += t(($) => $.pricingv2["Decrease by "]);
        }
        if (plan.relationship_rules.max_price.unit === "percent") {
          rule += `${plan.relationship_rules.max_price.value.toFixed(2)}%`;
        } else {
          rule += `${formatCurrency(plan.relationship_rules.max_price.value, {
            marketPlace: marketPlace,
          })}`;
        }

        const p = getPricingPlan(plan.relationship);
        return (
          <>
            <div>
              {formatCurrency(getPrice(plan, p.best_price, false), {
                marketPlace: marketPlace,
              })}
            </div>
            <small>
              <span>
                {formatCurrency(p.best_price, {
                  marketPlace: marketPlace,
                })}
              </span>{" "}
              <span>{rule}</span>
            </small>
          </>
        );
      }
      return `${formatCurrency(plan.max_price, {
        marketPlace: marketPlace,
      })}`;
    }
    return formatCurrency(plan.max_price, {
      marketPlace: marketPlace,
    });
  };

  useEffect(() => {
    if (parentPlan) {
      calcNewPrice();
    }

    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [
    parentPlan,
    selectedPlan,
    relatedMinAction,
    relatedMinUnit,
    relatedMinValue,
    relatedMaxAction,
    relatedMaxUnit,
    relatedMaxValue,
  ]);

  const chartOptions = useMemo(() => {
    if (!data || data.length <= 1) {
      setTab("table_view");
      return null;
    }
    function transformArray(arr) {
      const result = [];

      if (!arr) return result;

      arr.forEach((item) => {
        if (item.relationship !== null) {
          result.push([item.relationship.toString(), item.id.toString()]);
        }
      });

      return result;
    }

    function createNodes(arr) {
      const result = [];

      if (!arr) return result;

      arr.forEach((item) => {
        let relationship = item;
        for (let i = 0; i < arr.length; i++) {
          if (arr[i].id === item.relationship) {
            relationship = arr[i];
            break;
          }
        }

        result.push({
          id: item.id.toString(),
          name: cut(item.plan_name, 20),
          title: `${t(($) => $.pricingv2["Price"])}: ${formatCurrency(item.best_price, {
            marketPlace: marketPlace,
          })}<br/><small>${t(($) => $.pricingv2["Min"])}: ${formatCurrency(
            getPrice(item, relationship.best_price, true),
            {
              marketPlace: marketPlace,
            },
          )} - ${t(($) => $.pricingv2["Max"])}: ${formatCurrency(getPrice(item, relationship.best_price, false), {
            marketPlace: marketPlace,
          })}</small>`,
          image: `http://localhost:8000/api/productimage/?pricingplan=${item.id}`,
        });
      });

      return result;
    }

    return {
      chart: {
        height: 600,
        inverted: false,
      },

      title: {
        text: null,
      },

      accessibility: {
        point: {
          descriptionFormat:
            "{add index 1}. {toNode.name}" +
            "{#if (ne toNode.name toNode.id)}, {toNode.id}{/if}, " +
            "reports to {fromNode.id}",
        },
      },
      series: [
        {
          type: "organization",
          name: "Highsoft",
          keys: ["from", "to"],
          data: transformArray(data),
          levels: [
            {
              level: 0,
              color: trellisPalette[3],
              dataLabels: {
                color: "#fff",
              },
              height: 25,
            },
            {
              level: 1,
              color: trellisPalette[6],
              dataLabels: {
                color: "black",
              },
              height: 25,
            },
            {
              level: 2,
              color: trellisPalette[5],
            },
            {
              level: 4,
              color: "#359154",
            },
          ],
          nodes: createNodes(data),
          link: {
            color: "#000",
            lineWidth: 3,
          },
          point: {
            events: {
              click: function () {
                alert(t(($) => $.pricingv2["Clicked on {name}"], { name: this.name }));
              },
            },
          },
          colorByPoint: false,
          color: "#fff",
          dataLabels: {
            color: "#fff",
          },
          nodeWidth: "auto",
        },
      ],
      tooltip: {
        outside: true,
      },
      exporting: {
        allowHTML: true,
        sourceWidth: 600,
      },
    };
  }, [data, marketPlace, t]);

  const [columns, columnSelectorProps] = useColumnSelector(() => {
    const columns = [
      {
        id: "PricingPlan",
        label: t(($) => $.pricingv2["Pricing Plan"]),
        Header: () => (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              textAlign: "left",
            }}
          >
            <span style={{ position: "relative" }}>{t(($) => $.pricingv2["Pricing Plan"])}</span>
          </div>
        ),
        headerClassName: "wordwrap",
        headerStyle: { textAlign: "left" },
        style: { textAlign: "left" },
        accessor: "plan_name",
        Cell: (props) => {
          const image = `${URL_PREFIX}/api/productimage/?pricingplan=${props.original.id}`;
          return (
            <a
              href={`/user/pricing/plan/${props.original.id}`}
              rel="noopener noreferrer"
              style={{ display: "flex", alignItems: "center", textDecoration: "none" }}
            >
              <div
                style={{
                  flexShrink: 0,
                  width: 40,
                  height: 40,
                  marginRight: 8,
                  background: `url(${image}) no-repeat center / contain, url("${NoImage}") no-repeat center / contain`,
                }}
              />
              <span>
                {props.original.id} : {props.value}
              </span>
            </a>
          );
        },
      },
      {
        id: "Type",
        label: t(($) => $.pricingv2["Type"]),
        Header: () => (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              textAlign: "center",
            }}
          >
            <p style={{ position: "relative" }}></p>
            <span style={{ position: "relative", paddingRight: "1rem" }}>{t(($) => $.pricingv2["Parent"])}</span>
          </div>
        ),
        headerClassName: "wordwrap",
        headerStyle: { textAlign: "center" },
        style: { textAlign: "center" },
        accessor: "id",
        Cell: (props) => {
          return (
            <a href={`/user/pricing/plan/${props.original.relationship}`} rel="noopener noreferrer">
              <span>{props.original.relationship}</span>
            </a>
          );
        },
      },
      {
        id: "Price",
        label: t(($) => $.pricingv2["Price"]),
        Header: () => (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              textAlign: "center",
            }}
          >
            <p style={{ position: "relative" }}></p>
            <span style={{ position: "relative", paddingRight: "1rem" }}>{t(($) => $.pricingv2["Price"])}</span>
          </div>
        ),
        headerClassName: "wordwrap",
        headerStyle: { textAlign: "center" },
        style: { textAlign: "center" },
        accessor: "best_price",
        Cell: (props) => {
          return formatCurrency(props.original.best_price, {
            marketPlace: marketPlace,
          });
        },
      },
      {
        id: "MinPrice",
        label: t(($) => $.pricingv2["Minimum Price"]),
        Header: () => (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              textAlign: "center",
            }}
          >
            <p style={{ position: "relative" }}></p>
            <span style={{ position: "relative", paddingRight: "1rem" }}>{t(($) => $.pricingv2["Minimum Price"])}</span>
          </div>
        ),
        headerClassName: "wordwrap",
        headerStyle: { textAlign: "center" },
        style: { textAlign: "center" },
        accessor: "relationship",
        Cell: (props) => {
          return calcMinPrice(props.original);
        },
      },
      {
        id: "MaxPrice",
        label: t(($) => $.pricingv2["Maximum Price"]),
        Header: () => (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              textAlign: "center",
            }}
          >
            <p style={{ position: "relative" }}></p>
            <span style={{ position: "relative", paddingRight: "1rem" }}>{t(($) => $.pricingv2["Maximum Price"])}</span>
          </div>
        ),
        headerClassName: "wordwrap",
        headerStyle: { textAlign: "center" },
        style: { textAlign: "center" },
        accessor: "relationship",
        Cell: (props) => {
          return calcMaxPrice(props.original);
        },
      },
      {
        id: "Strategy",
        label: t(($) => $.pricingv2["Strategy"]),
        Header: () => (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              textAlign: "center",
            }}
          >
            <p style={{ position: "relative" }}></p>
            <span style={{ position: "relative", paddingRight: "1rem" }}>{t(($) => $.pricingv2["Strategy"])}</span>
          </div>
        ),
        headerClassName: "wordwrap",
        headerStyle: { textAlign: "center" },
        style: { textAlign: "center" },
        accessor: "relationship",
        Cell: (props) => {
          if (
            props.original.enroll_date &&
            props.original.configured_slider_value >= 0 &&
            props.original.configured_slider_value <= 0
          ) {
            return (
              <Badge
                className={`badge bg-trellis-purple text-end`}
                style={{
                  borderRadius: "5px",
                }}
              >
                <span>{t(($) => $.pricingv2.strategy["Balanced"])}</span>
              </Badge>
            );
          } else if (props.original.enroll_date && props.original.configured_slider_value < 0) {
            return (
              <Badge
                className={`badge bg-trellis-purple text-end`}
                style={{
                  borderRadius: "5px",
                }}
              >
                <span>{t(($) => $.pricingv2.strategy["Volume"])}</span>
              </Badge>
            );
          } else if (props.original.enroll_date && props.original.configured_slider_value > 0) {
            return (
              <Badge
                className={`badge bg-trellis-purple text-end`}
                style={{
                  borderRadius: "5px",
                }}
              >
                <span>{t(($) => $.pricingv2.strategy["Margin"])}</span>
              </Badge>
            );
          }
          return <span>{t(($) => $.pricingv2["n/a"])}</span>;
        },
      },
      {
        id: "Actions",
        label: t(($) => $.pricingv2["Actions"]),
        Header: () => (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              textAlign: "center",
            }}
          >
            <p style={{ position: "relative" }}></p>
            <span style={{ position: "relative", paddingRight: "1rem" }}>&nbsp;</span>
          </div>
        ),
        headerClassName: "wordwrap",
        headerStyle: { textAlign: "center" },
        style: { textAlign: "center" },
        accessor: "relationship",
        Cell: (props) => {
          if (!userCanUpdatePricingPlans) return null;
          const elements = [];
          elements.push(
            <span className="px-2">
              <button
                style={{ border: "0px", backgroundColor: "#fff" }}
                onClick={() => {
                  setNewMinPrice(props.original.best_price);
                  setNewMaxPrice(props.original.best_price);
                  setParentPlan(props.original);
                  setShowAddRelationship(true);
                }}
              >
                <FaPlusCircle />
              </button>
            </span>,
          );

          if (props.index !== 0) {
            elements.push(
              <span className="px-2">
                <button
                  style={{ border: "0px", backgroundColor: "#fff" }}
                  onClick={() => {
                    let parentPlan = null;
                    for (let i = 0; i < data.length; i++) {
                      if (data[i].id === props.original.relationship) {
                        parentPlan = data[i];
                        break;
                      }
                    }

                    setParentPlan(parentPlan);
                    setSelectedPlan(props.original);
                    setRelatedMode("edit");

                    const rules = props.original.relationship_rules;
                    if (rules.min_price) {
                      if (rules.min_price.action === "increase") {
                        setRelatedMinAction(actionTypeOptions[3]);
                      } else if (rules.min_price.action === "decrease") {
                        setRelatedMinAction(actionTypeOptions[2]);
                      } else if (rules.min_price.action === "match") {
                        setRelatedMinAction(actionTypeOptions[1]);
                      } else {
                        setRelatedMinAction(actionTypeOptions[0]);
                      }
                      setRelatedMinValue(rules.min_price.value);
                      setRelatedMinUnit(rules.min_price.unit);
                    }
                    if (rules.max_price) {
                      if (rules.max_price.action === "increase") {
                        setRelatedMaxAction(actionTypeOptions[3]);
                      } else if (rules.max_price.action === "decrease") {
                        setRelatedMaxAction(actionTypeOptions[2]);
                      } else if (rules.max_price.action === "match") {
                        setRelatedMaxAction(actionTypeOptions[1]);
                      } else {
                        setRelatedMaxAction(actionTypeOptions[0]);
                      }
                      setRelatedMaxValue(rules.max_price.value);
                      setRelatedMaxUnit(rules.max_price.unit);
                    }
                    setShowAddRelationship(true);
                  }}
                >
                  <FaEdit />
                </button>
              </span>,
            );

            elements.push(
              <span className="px-2">
                <button
                  style={{ border: "0px", backgroundColor: "#fff" }}
                  onClick={() => {
                    setSelectedPlan(props.original);
                    setShowRemoveModal(true);
                  }}
                >
                  <FaMinusCircle />
                </button>
              </span>,
            );
          }

          return elements;
        },
      },
    ];

    return columns;
  }, [colToggle, t]);

  if (isLoading || planIsLoading) return <Loading fullPage={false} height={`300px`} />;

  return (
    <>
      <Row>
        <Col className="p-4">
          {chartOptions && (
            <Row>
              <Col className="text-end">
                <BootstrapButton
                  style={{
                    borderLeft: "1px solid #aaa",
                    borderRight: 0,
                    borderTop: "1px solid #aaa",
                    borderBottom: "1px solid #aaa",
                    borderTopLeftRadius: "15px",
                    borderTopRightRadius: "0px",
                    borderBottomRightRadius: "0px",
                    borderBottomLeftRadius: "15px",
                    backgroundColor: tab === "chart_view" ? trellisPalette[4] : "#fff",
                    color: tab === "chart_view" ? "#fff" : null,
                  }}
                  className="fs-3"
                  onClick={() => setTab("chart_view")}
                >
                  <RiOrganizationChart />
                </BootstrapButton>
                <BootstrapButton
                  style={{
                    borderLeft: 0,
                    borderRight: "1px solid #aaa",
                    borderTop: "1px solid #aaa",
                    borderBottom: "1px solid #aaa",
                    borderTopLeftRadius: "0px",
                    borderTopRightRadius: "15px",
                    borderBottomRightRadius: "15px",
                    borderBottomLeftRadius: "0px",
                    backgroundColor: tab === "table_view" ? trellisPalette[4] : "#fff",
                    color: tab === "table_view" ? "#fff" : null,
                  }}
                  onClick={() => setTab("table_view")}
                  className="fs-3"
                >
                  <RiTable2 />
                </BootstrapButton>
              </Col>
            </Row>
          )}
          {chartOptions && tab === "chart_view" && <Chart options={chartOptions} />}
          {(chartOptions === null || tab === "table_view") && (
            <ReactTable
              ref={tableRef}
              data={data}
              className="pricing-table"
              PaginationComponent={(props) => {
                return (
                  <ReactTablePagination
                    {...props}
                    table={"pricing-table"}
                    showFilters={false}
                    style={{ marginBottom: "2rem" }}
                    carouselLayout={true}
                    hideFilters={true}
                    updateSearch={setSearch}
                    clearSearchTermFilters={() => setSearch("")}
                    searchTerm={search}
                    columnSelectorProps={columnSelectorProps}
                    hideSearch={true}
                  />
                );
              }}
              columns={columns}
              defaultPageSize={20}
              // Below is needed to eliminate the no data message that covers table headers if no data to render
              NoDataComponent={() => null}
              minRows={0}
              showPaginationTop
              showPaginationBottom={false}
              filterable={false}
            />
          )}
        </Col>
      </Row>
      {userCanUpdatePricingPlans && (
        <>
          <Modal show={showRemoveModal} onHide={() => hideRemoveModal()} size="lg">
            <Modal.Header closeButton={true} className="py-0 px-5">
              <div className="fs-3 my-4 text-center">{t(($) => $.pricingv2["Remove Relationships"])}</div>
            </Modal.Header>
            <Modal.Body>
              {!savingRelationship && (
                <Row className="py-3 px-4">
                  <Col>
                    <p className="fs-4">
                      {t(
                        ($) =>
                          $.pricingv2["Removing this relationship will also eliminate all dependent relationships."],
                      )}
                    </p>
                    <p className="fs-4">{t(($) => $.pricingv2["Would you like to proceed?"])}</p>
                  </Col>
                </Row>
              )}

              {savingRelationship && (
                <Row>
                  <Col>
                    <Loading fullPage={false} height={`200px`} />
                  </Col>
                </Row>
              )}
            </Modal.Body>
            <Modal.Footer>
              <Row className="py-4" style={{ width: "100%" }}>
                <Col xs={6} className="mx-auto text-start">
                  <Button onPress={() => hideRemoveModal()}>{t(($) => $.pricingv2["Close"])}</Button>
                </Col>
                <Col xs={6} className="mx-auto text-start">
                  <Button onPress={() => sendRemoveRelationship()}>
                    {t(($) => $.pricingv2["Remove Relationship"])}
                  </Button>
                </Col>
              </Row>
            </Modal.Footer>
          </Modal>
          <Modal show={showAddRelationship} onHide={() => hideModal()} size="lg">
            <Modal.Header closeButton={true} className="py-0 px-5">
              <div className="fs-3 my-4 text-center">{t(($) => $.pricingv2["Add a New Relationship"])}</div>
            </Modal.Header>
            <Modal.Body>
              {!savingRelationship && relatedMode === "new" && (
                <Row>
                  <Col xs={2}>&nbsp;</Col>
                  <Col xs={8}>
                    <input
                      type="text"
                      className="form-control"
                      placeholder={t(($) => $.pricingv2["Search for a pricing plan"])}
                      onChange={(e) => {
                        setSelectedPlan(null);
                        const val = e.target.value.toLocaleLowerCase();
                        if (val === "") {
                          setFilteredPlans([]);
                        } else {
                          const filtered = planNames.filter((d) => {
                            return d.plan_name.toLowerCase().includes(val);
                          });
                          setFilteredPlans(filtered);
                        }
                      }}
                    />
                  </Col>
                  <Col xs={2}>&nbsp;</Col>
                </Row>
              )}
              {!savingRelationship && filteredPlans && filteredPlans.length > 0 && (
                <Row>
                  <Col>
                    <Table striped bordered hover condensed>
                      <thead>
                        <tr>
                          <th style={{ textAlign: "left" }}>{t(($) => $.pricingv2["Plan Name"])}</th>
                          <th>&nbsp;</th>
                        </tr>
                      </thead>
                      {filteredPlans.length > 10 && (
                        <tbody>
                          <tr>
                            <td colSpan="2">{t(($) => $.pricingv2["Too many results, please refine your search"])}</td>
                          </tr>
                        </tbody>
                      )}
                      {filteredPlans.length <= 10 && (
                        <tbody>
                          {filteredPlans &&
                            filteredPlans.map((d) => {
                              return (
                                <tr key={d.id}>
                                  <td style={{ textAlign: "left" }}>
                                    {d.plan_name} ({d.id})
                                  </td>
                                  <td style={{ width: "100px" }}>
                                    <Button
                                      onPress={() => {
                                        setNewMinPrice(d.min_price);
                                        setNewMaxPrice(d.max_price);
                                        setRelatedMinValue(100);
                                        setRelatedMaxValue(100);
                                        setRelatedMinAction(actionTypeOptions[3]);
                                        setSelectedPlan(d);
                                        setFilteredPlans([]);
                                      }}
                                    >
                                      {t(($) => $.pricingv2["Select"])}
                                    </Button>
                                  </td>
                                </tr>
                              );
                            })}
                        </tbody>
                      )}
                    </Table>
                  </Col>
                </Row>
              )}

              {!savingRelationship && selectedPlan && (
                <>
                  <Row className="py-5 px-4">
                    <Col>
                      <span className="fs-4 fw-bold">{t(($) => $.pricingv2["Selected Plan"])}</span>
                      <Table striped bordered hover condensed>
                        <thead>
                          <tr>
                            <th style={{ textAlign: "left" }}>{t(($) => $.pricingv2["Rel"])}</th>
                            <th style={{ textAlign: "left" }}>{t(($) => $.pricingv2["Plan Name"])}</th>
                            <th style={{ textAlign: "center" }}>{t(($) => $.pricingv2["Current Price"])}</th>
                            <th style={{ textAlign: "center" }}>{t(($) => $.pricingv2["Min Price"])}</th>
                            <th style={{ textAlign: "center" }}>{t(($) => $.pricingv2["Max Price"])}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {parentPlan && (
                            <tr>
                              <td style={{ textAlign: "left" }}>{t(($) => $.pricingv2["Parent"])}</td>
                              <td style={{ textAlign: "left" }}>
                                {parentPlan?.plan_name} ({parentPlan?.id})
                              </td>
                              <td style={{ textAlign: "center" }}>
                                {formatCurrency(parentPlan?.best_price, {
                                  marketPlace: marketPlace,
                                })}
                              </td>
                              <td style={{ textAlign: "center" }}>
                                {formatCurrency(parentPlan?.min_price, {
                                  marketPlace: marketPlace,
                                })}
                              </td>
                              <td style={{ textAlign: "center" }}>
                                {formatCurrency(parentPlan?.max_price, {
                                  marketPlace: marketPlace,
                                })}
                              </td>
                            </tr>
                          )}
                          {selectedPlan && (
                            <tr>
                              <td style={{ textAlign: "left" }}>{t(($) => $.pricingv2["Selected"])}</td>
                              <td style={{ textAlign: "left" }}>
                                {selectedPlan?.plan_name} ({selectedPlan?.id})
                              </td>
                              <td style={{ textAlign: "center" }}>
                                {formatCurrency(selectedPlan?.best_price, {
                                  marketPlace: marketPlace,
                                })}
                              </td>
                              <td style={{ textAlign: "center" }}>
                                {formatCurrency(selectedPlan?.min_price, {
                                  marketPlace: marketPlace,
                                })}
                              </td>
                              <td style={{ textAlign: "center" }}>
                                {formatCurrency(selectedPlan?.max_price, {
                                  marketPlace: marketPlace,
                                })}
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </Table>
                    </Col>
                  </Row>
                  <Row className="px-4">
                    <Col xs={12} className="text-start">
                      <span className="fs-4 fw-bold">{t(($) => $.pricingv2["Relationship Rules"])}</span>
                      <Table striped bordered hover condensed>
                        <tbody>
                          <tr>
                            <td style={{ textAlign: "left" }}>
                              {t(($) => $.pricingv2["I want the minimum price to be"])}
                            </td>
                            <td style={{ textAlign: "left", width: "150px" }}>
                              <NumberInput
                                isDisabled={relatedMinAction.value === "match"}
                                value={relatedMinValue}
                                onChange={setRelatedMinValue}
                                minValue={0}
                              />
                            </td>
                            <td style={{ textAlign: "left" }}>
                              <Row>
                                <Col xs={6} className="text-center">
                                  <Checkbox
                                    label={<span>&nbsp;%</span>}
                                    value={relatedMinUnit === "percent"}
                                    onChange={() => {
                                      setRelatedMinUnit("percent");
                                    }}
                                    isDisabled={relatedMinAction.value === "match"}
                                  />
                                </Col>
                                <Col xs={6} className="text-center">
                                  <Checkbox
                                    label={<span>&nbsp;$</span>}
                                    value={relatedMinUnit === "currency"}
                                    onChange={() => {
                                      setRelatedMinUnit("currency");
                                    }}
                                    isDisabled={relatedMinAction.value === "match"}
                                  />
                                </Col>
                              </Row>
                            </td>
                            <td>
                              {t(($) => $.pricingv2["of {price}"], {
                                price: formatCurrency(parentPlan?.best_price, {
                                  marketPlace: marketPlace,
                                }),
                              })}
                            </td>
                            <td className="text-center">
                              <span>
                                {formatCurrency(newMinPrice, {
                                  marketPlace: marketPlace,
                                })}
                              </span>
                              <div>
                                <small>{t(($) => $.pricingv2["New minimum price"])}</small>
                              </div>
                            </td>
                          </tr>

                          <tr>
                            <td style={{ textAlign: "left" }}>
                              {t(($) => $.pricingv2["I want the maximum price to be"])}
                            </td>
                            <td style={{ textAlign: "left", width: "150px" }}>
                              <NumberInput
                                isDisabled={relatedMaxAction.value === "match"}
                                value={relatedMaxValue}
                                onChange={setRelatedMaxValue}
                                minValue={0}
                              />
                            </td>
                            <td style={{ textAlign: "left" }}>
                              <Row>
                                <Col xs={6} className="text-center">
                                  <Checkbox
                                    className={`text-center`}
                                    label={<span>&nbsp;%</span>}
                                    value={relatedMaxUnit === "percent"}
                                    onChange={() => {
                                      setRelatedMaxUnit("percent");
                                    }}
                                    isDisabled={relatedMaxAction.value === "match"}
                                  />
                                </Col>
                                <Col xs={6} className="text-center">
                                  <Checkbox
                                    className={`text-center`}
                                    label={<span>&nbsp;$</span>}
                                    value={relatedMaxUnit === "currency"}
                                    onChange={() => {
                                      setRelatedMaxUnit("currency");
                                    }}
                                    isDisabled={relatedMaxAction.value === "match"}
                                  />
                                </Col>
                              </Row>
                            </td>
                            <td>
                              {t(($) => $.pricingv2["of {price}"], {
                                price: formatCurrency(parentPlan?.best_price, {
                                  marketPlace: marketPlace,
                                }),
                              })}
                            </td>
                            <td className="text-center">
                              <span>
                                {formatCurrency(newMaxPrice, {
                                  marketPlace: marketPlace,
                                })}
                              </span>
                              <div>
                                <small>{t(($) => $.pricingv2["New maximum price"])}</small>
                              </div>
                            </td>
                          </tr>
                        </tbody>
                      </Table>
                    </Col>
                  </Row>
                </>
              )}

              {savingRelationship && (
                <Row>
                  <Col>
                    <Loading fullPage={false} height={`200px`} />
                  </Col>
                </Row>
              )}
            </Modal.Body>
            <Modal.Footer>
              <Row className="py-4" style={{ width: "100%" }}>
                <Col xs={6} className="mx-auto text-start">
                  <Button onPress={() => hideModal()}>{t(($) => $.pricingv2["Close"])}</Button>
                </Col>
                <Col xs={6} className="mx-auto text-start">
                  <Button onPress={() => sendRelated()} isDisabled={selectedPlan === null || savingRelationship}>
                    {t(($) => $.pricingv2["Save Relationship"])}
                  </Button>
                </Col>
              </Row>
            </Modal.Footer>
          </Modal>
        </>
      )}
    </>
  );
};

export default RelationshipSection;
